import React, { useState } from "react";
import publicApi from "@/lib/publicApi";
import { toast } from "sonner";
import {
  Loader2, HeartHandshake, ArrowRight, ShieldCheck, Sparkles, ArrowLeft, UserPlus,
} from "lucide-react";

const RELATIONSHIPS = [
  "Daughter", "Son", "Spouse / Partner", "Daughter-in-law", "Son-in-law",
  "Grandchild", "Niece / Nephew", "Sibling", "Friend", "Other family member", "Other",
];

// Warm 3-step "create your free account" flow for a temporary guest, shown inside the guest room.
export default function GuestConvertCard({ token, guest }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    full_name: guest?.name || "", email: guest?.email || "", phone: "",
    relationship_to_loved_one: "", password: "", confirm: "", consent: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (form.password.length < 8) { toast.error("Please choose a password with at least 8 characters."); return; }
    if (form.password !== form.confirm) { toast.error("Those passwords don't match."); return; }
    if (!form.consent) { toast.error("Please check the box to create your free account."); return; }
    setBusy(true);
    try {
      const { data } = await publicApi.post("/guest/convert",
        { email: form.email.trim().toLowerCase(), password: form.password, full_name: form.full_name.trim(),
          phone: form.phone.trim() || null, relationship_to_loved_one: form.relationship_to_loved_one || null, consent: true },
        { headers: { Authorization: `Bearer ${token}` } });
      localStorage.setItem("mmm_token", data.access_token);
      toast.success("Your free account is ready. Welcome!");
      window.location.href = "/portal";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "We couldn't create your account. Please try again.");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div className="rounded-2xl border border-[#9C1E22]/25 bg-gradient-to-br from-[#FBEDED] to-white p-5" data-testid="guest-convert-cta">
        <div className="flex items-start gap-3">
          <span className="h-11 w-11 rounded-xl bg-[#9C1E22] text-white flex items-center justify-center shrink-0"><HeartHandshake size={20} /></span>
          <div className="flex-1">
            <div className="font-display font-semibold text-[16px] text-[#1C4358] flex items-center gap-1.5"><Sparkles size={15} /> Keep everything in one place</div>
            <p className="text-[14px] text-[#4B5563] mt-1">Create a free MovingMyMama account so you don't have to start over. Your meetings, documents, and next steps stay saved and ready whenever you return.</p>
            <button onClick={() => setOpen(true)} data-testid="guest-convert-open-btn"
              className="mmm-tap mt-3 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white font-semibold text-[14px]">
              <UserPlus size={16} /> Create my free account
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#E6EDF2] bg-white p-5" data-testid="guest-convert-form">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-semibold text-[16px] text-[#1C4358]">Create your free account</div>
        <div className="text-[12px] text-[#9AA5B1]">Step {step} of 2</div>
      </div>

      {step === 1 ? (
        <div className="space-y-3" data-testid="guest-convert-step-1">
          <div>
            <label className="text-[13px] font-medium text-[#252221]">Your name</label>
            <input data-testid="convert-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)}
              className="mt-1 w-full h-12 rounded-xl border border-[#D6E0E7] px-3 text-[15px] outline-none focus:border-[#1C4358]" placeholder="First and last name" />
          </div>
          <div>
            <label className="text-[13px] font-medium text-[#252221]">Email</label>
            <input data-testid="convert-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
              className="mt-1 w-full h-12 rounded-xl border border-[#D6E0E7] px-3 text-[15px] outline-none focus:border-[#1C4358]" placeholder="you@email.com" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[13px] font-medium text-[#252221]">Phone (optional)</label>
              <input data-testid="convert-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                className="mt-1 w-full h-12 rounded-xl border border-[#D6E0E7] px-3 text-[15px] outline-none focus:border-[#1C4358]" placeholder="(555) 555-5555" />
            </div>
            <div>
              <label className="text-[13px] font-medium text-[#252221]">You are the loved one's…</label>
              <select data-testid="convert-relationship" value={form.relationship_to_loved_one} onChange={(e) => set("relationship_to_loved_one", e.target.value)}
                className="mt-1 w-full h-12 rounded-xl border border-[#D6E0E7] px-2 text-[15px] outline-none focus:border-[#1C4358] bg-white">
                <option value="">Select…</option>
                {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <button onClick={() => { if (!form.email.trim()) { toast.error("Please enter your email."); return; } setStep(2); }}
            data-testid="convert-continue-btn" className="mmm-tap w-full h-12 rounded-full bg-[#1C4358] hover:bg-[#163545] text-white font-semibold inline-flex items-center justify-center gap-2">
            Continue <ArrowRight size={16} />
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="guest-convert-step-2">
          <div>
            <label className="text-[13px] font-medium text-[#252221]">Create a password</label>
            <input data-testid="convert-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)}
              className="mt-1 w-full h-12 rounded-xl border border-[#D6E0E7] px-3 text-[15px] outline-none focus:border-[#1C4358]" placeholder="At least 8 characters" />
          </div>
          <div>
            <label className="text-[13px] font-medium text-[#252221]">Confirm password</label>
            <input data-testid="convert-confirm" type="password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)}
              className="mt-1 w-full h-12 rounded-xl border border-[#D6E0E7] px-3 text-[15px] outline-none focus:border-[#1C4358]" placeholder="Re-type your password" />
          </div>
          <label className="flex items-start gap-2 text-[13px] text-[#4B5563] cursor-pointer">
            <input data-testid="convert-consent" type="checkbox" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#9C1E22]" />
            <span>I'd like to create a free MovingMyMama account and I agree to the terms & privacy policy. This keeps my family's information saved securely for me.</span>
          </label>
          <div className="flex gap-2">
            <button onClick={() => setStep(1)} data-testid="convert-back-btn" className="mmm-tap h-12 px-4 rounded-full border border-[#D6E0E7] text-[#1C4358] font-medium inline-flex items-center gap-1.5"><ArrowLeft size={15} /> Back</button>
            <button onClick={submit} disabled={busy} data-testid="convert-submit-btn"
              className="mmm-tap flex-1 h-12 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white font-semibold inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 size={17} className="animate-spin" /> : <ShieldCheck size={17} />} Create my free account
            </button>
          </div>
          <p className="text-[12px] text-[#9AA5B1] flex items-center gap-1.5"><ShieldCheck size={13} className="text-[#2F7D5A]" /> Your information is private and only for you.</p>
        </div>
      )}
    </div>
  );
}
